import * as Yup from 'yup';
import { DataLists } from '../../services/data/data.types';
import { InformativeReunionStatus, Stakeholder } from 'services/person/person.types';
import { FormErrorMessages, nameSchema, phoneSchema } from 'utils/formValidation';

export const createNewPersonFirstStepSchema = (datalists: DataLists) =>
    Yup.object().shape({
        firstname: nameSchema,
        lastname: nameSchema,
        department: Yup.string()
            .oneOf(Object.keys(datalists?.departments || {}), FormErrorMessages.department_invalid)
            .optional(),
        phone: Yup.string()
            .matches(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, FormErrorMessages.phone_invalid)
            .required(FormErrorMessages.required),
        email: Yup.string().email(FormErrorMessages.email_invalid).optional(),
        genre: Yup.string()
            .oneOf(Object.keys(datalists?.person_genres || {}), FormErrorMessages.genre_invalid)
            .optional(),
        born_date: Yup.string()
            .matches(/^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/, FormErrorMessages.born_date_invalid)
            .optional(),
        informative_reunion: Yup.object({
            done: Yup.boolean(),
            date: Yup.string()
                .matches(/^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/, FormErrorMessages.informative_reunion_date_invalid)
                .optional(),
            location: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional(),
            status: Yup.string().oneOf(Object.values(InformativeReunionStatus), FormErrorMessages.informative_reunion_status_invalid).optional(),
            supervisor: Yup.mixed().optional(),
            department: Yup.string()
                .oneOf(Object.keys(datalists?.departments || {}), FormErrorMessages.informative_reunion_department_invalid)
                .optional()
        }),
        responsible_person: Yup.mixed().optional(),
        address: Yup.object({
            address: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional(),
            address_add: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional(),
            city: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional(),
            zipcode: Yup.string()
                .matches(/^\d{5}$/, FormErrorMessages.zipcode_invalid)
                .optional(),
            country: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional()
        }).optional(),
        source: Yup.string()
            .oneOf(Object.keys(datalists?.person_sources || {}), FormErrorMessages.source_invalid)
            .optional(),
        initial_request: Yup.string().min(3, FormErrorMessages.min_characters).max(2000, FormErrorMessages.max_characters).optional(),
        stakeholders: Yup.array<Stakeholder>()
            .of(
                Yup.object({
                    main_contact: Yup.boolean().optional(),
                    legal_contact: Yup.boolean().optional(),
                    firstname: Yup.string().min(3, FormErrorMessages.min_characters).max(50, FormErrorMessages.max_characters).optional(),
                    lastname: Yup.string().min(3, FormErrorMessages.min_characters).max(50, FormErrorMessages.max_characters).optional(),
                    phone: Yup.string()
                        .matches(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, FormErrorMessages.phone_invalid)
                        .optional(),
                    email: Yup.string().email(FormErrorMessages.email_invalid).optional(),
                    status: Yup.string()
                        .oneOf(Object.keys(datalists?.stakeholder_status || {}), FormErrorMessages.status_invalid)
                        .optional(),
                    address: Yup.object({
                        address: Yup.string()
                            .min(3, FormErrorMessages.min_characters)
                            .max(200, FormErrorMessages.max_characters)
                            .optional(),
                        address_add: Yup.string()
                            .min(3, FormErrorMessages.min_characters)
                            .max(200, FormErrorMessages.max_characters)
                            .optional(),
                        city: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional(),
                        zipcode: Yup.string()
                            .matches(/^\d{4,}$/, FormErrorMessages.zipcode_invalid)
                            .optional(),
                        country: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional()
                    }).optional()
                })
            )
            .optional()
    });

export const createNewPersonSecondStepSchema = () =>
    Yup.object().shape({
        firstname: nameSchema,
        lastname: nameSchema,
        phone: phoneSchema,
        documents: Yup.array()
            .of(
                Yup.object({
                    id: Yup.number().required(),
                    original_filename: Yup.string()
                        .min(3, FormErrorMessages.min_characters)
                        .max(2000, FormErrorMessages.max_characters)
                        .required(),
                    label: Yup.string().min(3, FormErrorMessages.min_characters).max(200, FormErrorMessages.max_characters).optional(),
                    level: Yup.string().oneOf(['public', 'private']).required()
                })
            )
            .optional()
    });

export const createNewPersonThirdStepSchema = (datalists: DataLists) => {
    return Yup.object().shape({
        firstname: nameSchema,
        lastname: nameSchema,
        disorders: Yup.object({
            mdph: Yup.string().oneOf(['yes', 'no', 'pending'], FormErrorMessages.disorder_invalid).optional(),
            main: Yup.string()
                .nullable()
                .transform((value) => (!value ? null : value))
                .oneOf([...Object.keys(datalists?.disorders || {}), null], FormErrorMessages.disorder_invalid),
            secondaries: Yup.array().of(
                Yup.string()
                    .nullable()
                    .transform((value) => (!value ? null : value))
                    .oneOf([...Object.keys(datalists?.disorders || {}), null], FormErrorMessages.disorder_invalid)
            )
        }).optional()
    });
};
